/**
 * NFC Solutions — Windows EXE (Tauri) update check. Session 30.25.
 *
 * Mirror of apkUpdater.js for the desktop shell. Polls
 * `GET /api/exe/latest` every 6 hours (plus once shortly after launch)
 * and, when the server advertises a newer semver than the version this
 * build was stamped with (`REACT_APP_VERSION`, set by build-exe.yml from
 * the git tag), hands the metadata to the caller so it can show a
 * "Download update" toast. Downloading opens the installer in the
 * system browser via Tauri's shell plugin (`shell:allow-open` is granted
 * in src-tauri/capabilities/default.json); the user runs the installer,
 * which upgrades in place and keeps IndexedDB / localStorage.
 *
 * Browser-safe: every export is a no-op outside the Tauri shell.
 */

const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
const FIRST_CHECK_DELAY_MS = 20 * 1000;
const DISMISS_MEMORY_MS = 6 * 60 * 60 * 1000;
const DISMISS_KEY = "nfcs:exe-update:deferred-at";

export function isTauriShell() {
  try {
    return typeof window !== "undefined" && !!(window.__TAURI__ || window.__TAURI_INTERNALS__);
  } catch (_) {
    return false;
  }
}

export function semverToCode(v) {
  const parts = String(v || "0.0.0").split("-")[0].split(".").map((x) => parseInt(x, 10) || 0);
  return (parts[0] || 0) * 1_000_000 + (parts[1] || 0) * 1_000 + (parts[2] || 0);
}

export function installedExeVersion() {
  return process.env.REACT_APP_VERSION || "0.0.0";
}

export async function fetchExeLatest() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return null;
  try {
    const res = await fetch(`${base.replace(/\/$/, "")}/api/exe/latest`, { cache: "no-store" });
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
}

/** Returns the latest metadata when it is newer than this build, else null. Never throws. */
export async function checkForExeUpdate() {
  if (!isTauriShell()) return null;
  const installed = semverToCode(installedExeVersion());
  if (installed <= 0) return null; // unstamped dev build — nothing honest to compare
  const meta = await fetchExeLatest();
  if (!meta || !meta.available || !meta.download_url) return null;
  const latest = Number(meta.version_code) || semverToCode(meta.version_name);
  if (latest <= 0 || latest <= installed) return null;
  return meta;
}

export async function openExeDownload(url) {
  if (!url) return false;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("plugin:shell|open", { path: url });
    return true;
  } catch (_) {
    try { window.open(url, "_blank", "noopener"); return true; } catch (_e) { return false; }
  }
}

export function recordExeUpdateDeferred() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* noop */ }
}

function _recentlyDeferred() {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t > 0 && Date.now() - t < DISMISS_MEMORY_MS;
  } catch (_) {
    return false;
  }
}

/** Arm the periodic check. Returns a stop function. */
export function armPeriodicExeUpdateCheck(onUpdate) {
  if (!isTauriShell() || typeof onUpdate !== "function") return () => {};
  let stopped = false;
  const tick = async () => {
    if (stopped || _recentlyDeferred()) return;
    try {
      const meta = await checkForExeUpdate();
      if (meta && !stopped) onUpdate(meta);
    } catch (_) { /* noop */ }
  };
  const first = setTimeout(tick, FIRST_CHECK_DELAY_MS);
  const timer = setInterval(tick, CHECK_INTERVAL_MS);
  return () => { stopped = true; clearTimeout(first); clearInterval(timer); };
}
